import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  OnDestroy,
  Signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { SharedModule } from '@informally/admin/shared/shared.module';
import { RegisterComponent, VerifyService } from '@suvo-bi-users';
import { lastValueFrom } from 'rxjs';

@Component({
  standalone: true,
  imports: [MatCardModule, MatButtonModule, SharedModule],
  template: `
    <div class="outer-container">
      <mat-card class="register-card" appearance="outlined">
        <div class="register-card-content" [class.working]="working">
          <div class="register-header">
            <a href="https://informally.io" class="brand-link" aria-label="Informally home">
              <img src="/assets/logo-informally.svg" alt="Informally" />
            </a>
            <h1>Welcome to informally</h1>
            @if (!registrationSuccessful) {
              <p>One more step. Create your informally account and get started for free.</p>
            } @else {
              <p>Your account has been created.</p>
            }
          </div>

          @if (!registrationSuccessful) {
            <lib-page-register
              #libRegister
              [validateOnly]="true"
              [externalLoading]="working"
              [showFields]="showFields"
              [consentBoxes]="consentBoxes"
              [marketingInfo]="'checkbox'"
              [loginRoute]="['/login']"
              [preserveLoginQueryParams]="true"
              (registerStatusChange)="register($event)"
            />
          }

          @if (message) {
            <p class="message" [class.error]="message.error">{{ message.value }}</p>
          }

          @if (registrationSuccessful) {
            <div class="success-state">
              @if (registeredEmail) {
                <p class="success-detail">
                  We sent the verification email to <strong>{{ registeredEmail }}</strong
                  >.
                </p>
              }
              <p class="success-detail">
                Check your inbox (and spam folder), then log in once verified.
              </p>
              <p class="success-detail">
                Ready to continue?
                <a class="login-link-inline" href="" (click)="$event.preventDefault(); goToLogin()"
                  >Log in here</a
                >.
              </p>
              <div class="actions">
                <button mat-raised-button color="primary" type="button" (click)="goToLogin()">
                  Go to login
                </button>
              </div>
            </div>
          }

          @if (emailAlreadyInUse && !registrationSuccessful) {
            <div class="actions">
              <button mat-stroked-button type="button" (click)="goToLogin()">Go to login</button>
              <button mat-raised-button color="primary" type="button" (click)="loginInstead()">
                Log in and continue
              </button>
            </div>
          }
        </div>
      </mat-card>
    </div>
  `,
  styles: [
    `
      .outer-container {
        min-height: calc(100vh - max(24px, 10vh));
        width: 100%;
        background-color: #f6f7fb;
        padding: max(24px, 10vh) 16px 0;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
      }

      .register-card {
        width: 100%;
        max-width: 560px;
        margin: 0 auto;
        border-radius: 14px;
        border: 1px solid #e3e8f2;
        box-shadow: 0 14px 36px rgba(17, 24, 39, 0.09);
      }

      .register-card-content {
        padding: 34px 28px;
      }

      .register-header {
        max-width: none;
        width: 100%;
        margin: 0 0 24px;
        text-align: left;
      }

      .brand-link {
        display: inline-block;
        margin-top: 0;
      }

      .register-header h1 {
        margin: 20px 0 8px;
        line-height: 1.15;
        letter-spacing: -0.01em;
        color: #1f2a44;
      }

      .register-header p {
        margin: 0;
        color: #5c6478;
        font-size: 1rem;
        line-height: 1.45;
      }

      .message {
        margin: 16px 0 0;
        color: #445275;
        line-height: 1.4;
      }

      .error {
        color: #b42318;
      }

      .actions {
        margin-top: 16px;
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        flex-wrap: wrap;
      }

      .success-state {
        margin-top: 10px;
      }

      .success-detail {
        margin: 8px 0 0;
        color: #445275;
        line-height: 1.45;
      }

      .login-link-inline {
        color: #2e7ff2;
        text-decoration: none;
        font-weight: 600;
      }

      .login-link-inline:hover {
        text-decoration: underline;
      }

      .working {
        opacity: 50%;
        pointer-events: none;
      }

      @media (max-width: 560px) {
        .register-card-content {
          padding: 26px 18px;
        }

        .actions {
          justify-content: stretch;
        }

        .actions button {
          flex: 1 1 220px;
        }
      }
    `,
  ],
})
export class RegisterPageComponent implements OnDestroy, AfterViewInit {
  readonly showFields = [
    { name: 'firstName', required: true },
    { name: 'lastName', required: true },
  ];

  readonly consentBoxes = [
    {
      checkboxText:
        "I agree to Informally's Terms of Service and Privacy Policy, and consent to account creation.",
      required: true,
    },
  ];

  @ViewChild('libRegister')
  private readonly registerComponent!: RegisterComponent;

  private readonly queryParams: Signal<ParamMap | undefined>;

  private readonly tenantRecipe = computed(() => this.queryParams()?.get('recipe') || null);

  private readonly effect = effect(() => {
    if (!this.tenantRecipe()) {
      void this.router.navigate(['/new-shop']);
    }
  });

  working = false;

  emailAlreadyInUse = false;

  registrationSuccessful = false;

  registeredEmail = '';

  message: {
    value: string;
    error: boolean;
  } | null = null;

  constructor(
    activatedRoute: ActivatedRoute,
    private readonly fireAuth: AngularFireAuth,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly verifyService: VerifyService,
  ) {
    this.queryParams = toSignal(activatedRoute.queryParamMap);
  }

  ngAfterViewInit() {
    try {
      const onboardingRaw = localStorage.getItem('onboarding');
      if (!onboardingRaw) {
        return;
      }

      const { email }: { email?: string } = JSON.parse(onboardingRaw);
      if (email) {
        this.registerComponent.registerForm.controls.email.setValue(email);
      }
    } catch {
      // Ignore malformed local onboarding payloads.
    }
  }

  ngOnDestroy() {
    this.effect.destroy();
  }

  async register(registerStatusEvent: string) {
    // Library component is set to validate only
    // When it emits the event, we do the registration
    if (registerStatusEvent === 'REGISTER_ATTEMPT_COMPLETE') {
      try {
        this.working = true;
        this.emailAlreadyInUse = false;
        this.registrationSuccessful = false;
        this.registeredEmail = '';
        const body = this.buildRegistrationPayload();
        await lastValueFrom(this.http.post(`${environment.dataServiceApi}tenant/new`, body));
        const { email, password } = this.registerComponent.registerForm.value;
        await this.fireAuth.signInWithEmailAndPassword(email, password);
        await this.verifyService.sendVerificationEmail();
        this.registeredEmail = email || '';
        this.registrationSuccessful = true;
        this.message = {
          value:
            'A verification link has been sent to your email address. Please click the link to activate your account.',
          error: false,
        };
      } catch (e) {
        const error = e as { error?: { message?: string }; message?: string };
        this.emailAlreadyInUse = this.isEmailAlreadyInUseError(error);
        this.registrationSuccessful = false;
        this.registeredEmail = '';
        this.message = {
          value: this.emailAlreadyInUse
            ? 'An account already exists for this email address. Log in to continue and we will finish creating your shop.'
            : error.error?.message ||
              error.message ||
              'Something went wrong while creating your account.',
          error: true,
        };
      } finally {
        this.working = false;
      }
    }
  }

  goToLogin() {
    void this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
  }

  async loginInstead() {
    const { email, password } = this.registerComponent.registerForm.value as {
      email?: string;
      password?: string;
    };
    const tenantRecipeID = this.tenantRecipe();

    if (!email || !password) {
      this.message = { value: 'Enter your email and password to log in.', error: true };
      return;
    }
    if (!tenantRecipeID) {
      void this.router.navigate(['/new-shop']);
      return;
    }

    try {
      this.working = true;
      this.message = { value: 'Logging in…', error: false };

      await this.fireAuth.signInWithEmailAndPassword(email, password);

      const user = await this.fireAuth.currentUser;
      const token = await user?.getIdToken();

      this.message = { value: 'Creating your shop…', error: false };
      await lastValueFrom(
        this.http.post(
          `${environment.dataServiceApi}tenant/existing`,
          { tenantRecipeID },
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        ),
      );

      // Tenant roles/claims may have changed; force refresh so shop selection works immediately.
      await user?.getIdToken(true);

      await this.router.navigate(['/shops'], { queryParams: { autoRedirect: 'true' } });
    } catch (e) {
      const error = e as { error?: { message?: string }; message?: string };
      this.message = {
        value: error.error?.message || error.message || 'Login failed. Please try again.',
        error: true,
      };
    } finally {
      this.working = false;
    }
  }

  private isEmailAlreadyInUseError(e: any): boolean {
    const message = (e?.error?.message || e?.message || '').toString().toLowerCase();

    return (
      message.includes('email address is already in use') ||
      message.includes('email-already-exists') ||
      message.includes('auth/email-already-in-use')
    );
  }

  private buildRegistrationPayload(): {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    tenantRecipeID: string;
    termsAndConditionsConsent: boolean;
    marketingEmailsConsent?: boolean;
  } {
    const formValue = this.registerComponent.registerForm.value as {
      email?: string;
      firstName?: string;
      lastName?: string;
      password?: string;
      marketingAllowed?: boolean;
      consentBox0?: boolean;
    };

    return {
      email: formValue.email || '',
      firstName: formValue.firstName || '',
      lastName: formValue.lastName || '',
      password: formValue.password || '',
      tenantRecipeID: this.tenantRecipe() || '',
      termsAndConditionsConsent: formValue.consentBox0 === true,
      marketingEmailsConsent: formValue.marketingAllowed === true,
    };
  }
}
